import { z } from 'zod';
import { CHANNELS, INSTALLMENT_STATUSES, LANGUAGES, PERIODICITIES } from '../domain';
import { isoDateSchema } from './referentials';

// API publique pour les SI des compagnies (BA-048 à BA-051, spec 10) : clés et scopes,
// contrats et échéances par numéro de police, paiements, séquences, événements, webhooks.

export const API_SCOPES = [
  'read',
  'contracts:write',
  'installments:write',
  'payments:write',
] as const;
export type ApiScope = (typeof API_SCOPES)[number];

const millimes = z.number().int().min(0).max(1_000_000_000_000);

// --- Clés API (portail, admin) ---

export const apiKeyCreateSchema = z.object({
  label: z.string().trim().min(1).max(80),
  scopes: z.array(z.enum(API_SCOPES)).min(1),
});
export const apiKeySchema = z.object({
  id: z.string(),
  label: z.string(),
  prefix: z.string().nullable(),
  scopes: z.array(z.enum(API_SCOPES)),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  createdAt: z.string(),
});
export const apiKeyListSchema = z.object({ items: z.array(apiKeySchema) });
export const apiKeyCreatedSchema = apiKeySchema.extend({
  /** Affichée une seule fois : seule son empreinte est conservée. */
  key: z.string(),
});
export const apiRequestLogSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      apiKeyId: z.string(),
      method: z.string(),
      path: z.string(),
      status: z.number().int(),
      durationMs: z.number().int(),
      requestId: z.string().nullable(),
      at: z.string(),
    }),
  ),
});

// --- Contrats et échéances ---

export const publicContractSchema = z
  .object({
    productCode: z.string().trim().min(1).max(20),
    insurerCode: z.string().trim().max(20).optional(),
    policyholder: z.object({
      kind: z.enum(['person', 'company']).default('person'),
      firstName: z.string().trim().max(80).optional(),
      lastName: z.string().trim().max(80).optional(),
      companyName: z.string().trim().max(160).optional(),
      externalRef: z.string().trim().max(64).optional(),
      phone: z.string().max(40),
      email: z.string().max(254).optional(),
      cin: z.string().max(20).optional(),
      language: z.enum(LANGUAGES).optional(),
    }),
    agentCode: z.string().trim().max(20).optional(),
    insuredObject: z.string().trim().max(120).optional(),
    effectiveDate: isoDateSchema,
    expiryDate: isoDateSchema.optional(),
    periodicity: z.enum(PERIODICITIES).optional(),
    /** Prime annuelle en millimes (ADR-002). */
    premiumAmount: millimes,
    currency: z.literal('TND').default('TND'),
    externalRef: z.string().trim().max(64).optional(),
  })
  .refine(
    (c) =>
      c.policyholder.kind === 'company' ? !!c.policyholder.companyName : !!c.policyholder.lastName,
    {
      message: 'Nom de l’assuré requis (raison sociale pour une entreprise)',
      path: ['policyholder'],
    },
  );
export type PublicContract = z.infer<typeof publicContractSchema>;

export const publicBatchSchema = z.object({
  contracts: z
    .array(publicContractSchema.and(z.object({ policyNumber: z.string().trim().min(1).max(64) })))
    .min(1)
    .max(1000),
});
export const publicBatchAcceptedSchema = z.object({
  batchId: z.string(),
  accepted: z.number().int(),
});

export const publicInstallmentSchema = z.object({
  dueDate: isoDateSchema,
  amountDue: millimes,
  periodStart: isoDateSchema.optional(),
  periodEnd: isoDateSchema.optional(),
  sequenceNo: z.number().int().min(1).optional(),
  insurerCode: z.string().trim().max(20).optional(),
});

export const publicPaymentSchema = z.object({
  policyNumber: z.string().trim().min(1).max(64).optional(),
  installmentRef: z.string().trim().min(1).max(64).optional(),
  insurerCode: z.string().trim().max(20).optional(),
  dueDate: isoDateSchema.optional(),
  amount: millimes.refine((a) => a > 0, 'Montant positif requis'),
  paidAt: isoDateSchema,
  method: z.enum(['agency', 'bank', 'check', 'other']),
  /** Référence unique du paiement dans le SI : clé d'idempotence. */
  reference: z.string().trim().min(1).max(64),
});

export const publicInstallmentQuerySchema = z.object({
  status: z.enum(INSTALLMENT_STATUSES).optional(),
  due_from: isoDateSchema.optional(),
  due_to: isoDateSchema.optional(),
  updated_since: z.string().datetime().optional(),
  cursor: z.string().max(128).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

export const publicInstallmentRowSchema = z.object({
  id: z.string(),
  policyNumber: z.string(),
  externalRef: z.string().nullable(),
  sequenceNo: z.number().int(),
  dueDate: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  amountDue: z.number().int(),
  amountPaid: z.number().int(),
  currency: z.literal('TND'),
  status: z.enum(INSTALLMENT_STATUSES),
  updatedAt: z.string(),
});
export const publicInstallmentPageSchema = z.object({
  items: z.array(publicInstallmentRowSchema),
  nextCursor: z.string().nullable(),
});

export const publicContractViewSchema = z.object({
  policyNumber: z.string(),
  status: z.string(),
  insurerCode: z.string(),
  productCode: z.string(),
  effectiveDate: z.string(),
  expiryDate: z.string().nullable(),
  periodicity: z.enum(PERIODICITIES),
  premiumAmount: z.number().int(),
  currency: z.literal('TND'),
  policyholder: z.object({
    displayName: z.string(),
    externalRef: z.string().nullable(),
    preferredChannel: z.enum(CHANNELS),
    language: z.enum(LANGUAGES),
    optedOut: z.boolean(),
  }),
  installments: z.array(publicInstallmentRowSchema),
  sequences: z.array(
    z.object({
      id: z.string(),
      installmentId: z.string(),
      status: z.string(),
      currentStepNo: z.number().int(),
      nextRunAt: z.string().nullable(),
    }),
  ),
});

export const publicEventQuerySchema = z.object({
  /** Identifiant du dernier événement reçu (exclu). */
  since: z.string().max(64).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export const publicEventPageSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      type: z.string(),
      occurredAt: z.string(),
      data: z.record(z.string(), z.unknown()),
    }),
  ),
  nextSince: z.string().nullable(),
});

// --- Webhooks sortants ---

export const OUTGOING_EVENTS = [
  'installment.status_changed',
  'payment.confirmed',
  'payment.refunded',
  'sequence.started',
  'sequence.stopped',
  'sequence.escalated',
  'message.delivered',
  'message.failed',
  'policyholder.opted_out',
  'policyholder.channel_changed',
  'import.completed',
  'contract.renewed',
  'contract.renewal_declined',
  'claim.declared',
] as const;

export const webhookSubscriptionInputSchema = z.object({
  url: z
    .string()
    .url()
    .max(500)
    .refine((u) => u.startsWith('https://'), 'URL HTTPS requise'),
  description: z.string().trim().max(120).nullable().optional(),
  events: z.array(z.enum(OUTGOING_EVENTS)).min(1),
  active: z.boolean().optional(),
});
export const webhookSubscriptionSchema = z.object({
  id: z.string(),
  url: z.string(),
  description: z.string().nullable(),
  events: z.array(z.string()),
  active: z.boolean(),
  createdAt: z.string(),
});
export const webhookSubscriptionCreatedSchema = webhookSubscriptionSchema.extend({
  /** Secret de signature, affiché une seule fois. */
  secret: z.string(),
});
export const webhookSubscriptionListSchema = z.object({
  items: z.array(webhookSubscriptionSchema),
});
export const webhookDeliveryListSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      subscriptionId: z.string(),
      eventId: z.string(),
      eventType: z.string(),
      status: z.enum(['pending', 'delivered', 'failed', 'undelivered']),
      attempts: z.number().int(),
      nextAttemptAt: z.string().nullable(),
      lastStatusCode: z.number().int().nullable(),
      lastError: z.string().nullable(),
      deliveredAt: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
});
